import { useCallback, useMemo } from 'react';
import { PREF_KEYS, getUserPref, usePreference } from '../hooks/useUserPreferences';
import { useGlobalStore } from '../stores/globalStore';
import type { NavPageView, NavSectionView } from './navConfig';

/**
 * Pinned and Recent pages, listed in Home's panel (Jeff, 2026-10-22).
 *
 * Both are per user, through the user-preferences store (the one Size and Font use), and both
 * hold page KEYS (the config route, e.g. `/facilities/{facilityId}`), so they survive a facility
 * switch. What is shown is always worked out against the sections the user can see NOW: a page
 * their role, licence or facility no longer allows, or one greyed out here, is not listed, and
 * reappears if it becomes available again. Home's own pages are never pinned or recorded: they are
 * already at the top of Home's panel.
 */

/** Recent pages listed in Home's panel. */
export const RECENT_SHOWN = 6;
/** Kept a little longer than shown, so pages that drop out (pinned, no longer allowed) leave room. */
const RECENT_KEPT = 12;

/** `key` first, without an earlier visit to it, capped. */
export function withRecent(recent: readonly string[], key: string): string[] {
  return [key, ...recent.filter((k) => k !== key)].slice(0, RECENT_KEPT);
}

/** Adds `key` at the end (pin order) or takes it out. */
export function togglePinned(pinned: readonly string[], key: string): string[] {
  return pinned.includes(key) ? pinned.filter((k) => k !== key) : [...pinned, key];
}

/** The pages that can be pinned or recorded, by key: every openable page outside Home. */
export function pinnablePages(sections: readonly NavSectionView[]): Map<string, NavPageView> {
  const pages = new Map<string, NavPageView>();
  for (const section of sections) {
    if (section.id === 'home') continue;
    for (const page of section.pages) {
      if (!page.disabledReason && !pages.has(page.key)) pages.set(page.key, page);
    }
  }
  return pages;
}

/** The pinned pages the user can open now, in pin order. */
export function pinnedPagesOf(pinned: readonly string[], pages: ReadonlyMap<string, NavPageView>): NavPageView[] {
  return pinned.map((k) => pages.get(k)).filter((p): p is NavPageView => !!p);
}

/** The recent pages the user can open now, newest first, without pinned ones, at most RECENT_SHOWN. */
export function recentPagesOf(
  recent: readonly string[],
  pinned: readonly string[],
  pages: ReadonlyMap<string, NavPageView>,
): NavPageView[] {
  return recent
    .filter((k) => !pinned.includes(k))
    .map((k) => pages.get(k))
    .filter((p): p is NavPageView => !!p)
    .slice(0, RECENT_SHOWN);
}

export interface NavPins {
  /** Pinned pages to list, in pin order. */
  pinnedPages: NavPageView[];
  /** Recent pages to list, newest first. */
  recentPages: NavPageView[];
  /** True for a page that can carry a pin star (any openable page outside Home). */
  canPin: (page: NavPageView) => boolean;
  isPinned: (key: string) => boolean;
  togglePin: (key: string) => void;
  /** Puts a page at the front of Recent. Ignores pages that cannot be pinned (Home's, greyed out). */
  recordVisit: (key: string) => void;
}

export function useNavPins(sections: readonly NavSectionView[]): NavPins {
  const userId = useGlobalStore((s) => s.userId);
  const [pinned, setPinned] = usePreference<string[]>(PREF_KEYS.NAV_PINNED, []);
  const [recent, setRecent] = usePreference<string[]>(PREF_KEYS.NAV_RECENT, []);
  const pages = useMemo(() => pinnablePages(sections), [sections]);

  const togglePin = useCallback(
    // Read fresh: two stars pressed before a re-render must not lose one.
    (key: string) => setPinned(togglePinned(getUserPref<string[]>(userId, PREF_KEYS.NAV_PINNED, []), key)),
    [userId, setPinned],
  );
  const recordVisit = useCallback((key: string) => {
    if (!pages.has(key)) return;
    const current = getUserPref<string[]>(userId, PREF_KEYS.NAV_RECENT, []);
    if (current[0] === key) return;
    setRecent(withRecent(current, key));
  }, [pages, userId, setRecent]);

  return useMemo(() => ({
    pinnedPages: pinnedPagesOf(pinned, pages),
    recentPages: recentPagesOf(recent, pinned, pages),
    canPin: (page: NavPageView) => pages.has(page.key),
    isPinned: (key: string) => pinned.includes(key),
    togglePin,
    recordVisit,
  }), [pinned, recent, pages, togglePin, recordVisit]);
}
